'use client';

import { BuildProject } from 'features/build-project';
import { InstanceController } from 'features/instance-controller';
import { RerunProject } from 'features/rerun-project';
import { StartStopProject } from 'features/start-stop-project';
import type { Dispatch, SetStateAction } from 'react';
import { createContext, useContext, useState } from 'react';
import type { MutationOptions } from '@tanstack/react-query';
import type { MutationOptionsProps } from 'features/start-stop-project/ui/start-stop-project';
import type { Project } from '@/enteties/project/modal';
import { cva, type VariantProps } from 'class-variance-authority';
import type { StatusResponse } from 'shared/api/services/types';
import { Package, RotateCcw } from 'lucide-react';
import * as m from '@paraglide/messages';
interface ProjectControllerContextProps<TData> {
  mutationOptions: MutationOptions<TData, Error, number>;
  slug: string;
  project: Project;
  isTimedOut: boolean;
  setIsTimedOut: Dispatch<SetStateAction<boolean>>;
}
const ProjectControllerContext = createContext<ProjectControllerContextProps<any> | undefined>(undefined);
function useProjectControllerContext() {
  const context = useContext(ProjectControllerContext);
  if (!context) {
    throw new Error('useProjectControllerContext must be used within a ProjectControllerContext');
  }
  return context;
}
export const projectControllerButtonVariants = cva('inline-flex flex-row gap-2', {
  variants: {
    version: {
      default: 'items-end',
      center: 'items-center'
    }
  }
});
interface ProjectControllerButtonsProps<TData extends StatusResponse> extends MutationOptionsProps<TData>, VariantProps<typeof projectControllerButtonVariants> {
  children?: React.ReactNode;
  project: Project;
  slug: string;
}
export const ProjectControllerButtons = <TData extends StatusResponse,>({
  mutationOptions,
  slug,
  project,
  children,
  version = 'default'
}: ProjectControllerButtonsProps<TData>) => {
  const [isTimedOut, setIsTimedOut] = useState(false);
  return <ProjectControllerContext.Provider value={{
    mutationOptions,
    slug,
    project,
    isTimedOut,
    setIsTimedOut
  }} data-sentry-element="unknown" data-sentry-component="ProjectControllerButtons" data-sentry-source-file="project-controller-buttons.tsx">
      <div className={projectControllerButtonVariants({
      version
    })}>
        {children}
      </div>
    </ProjectControllerContext.Provider>;
};
ProjectControllerButtons.Instances = function ProjectControllerInstances({
  withLabel
}: {
  withLabel?: boolean;
}) {
  const {
    mutationOptions,
    slug,
    project,
    isTimedOut
  } = useProjectControllerContext();
  return <InstanceController mutationOptions={mutationOptions} withLabel={withLabel} slug={slug} project={project} isTimedOut={isTimedOut} />;
};
ProjectControllerButtons.StartStop = function ProjectControllerStartStop({
  stopBuildMutationOptions
}: {
  stopBuildMutationOptions?: MutationOptions<Project, Error, void>;
}) {
  const {
    mutationOptions,
    slug,
    project,
    isTimedOut
  } = useProjectControllerContext();
  return <StartStopProject mutationOptions={mutationOptions} stopBuildMutationOptions={stopBuildMutationOptions} slug={slug} project={project} isTimedOut={isTimedOut} />;
};
ProjectControllerButtons.BuildProject = function ProjectControllerBuildProject() {
  const {
    slug,
    project,
    isTimedOut,
    setIsTimedOut
  } = useProjectControllerContext();
  return <BuildProject slug={slug} project={project} isTimedOut={isTimedOut} setIsTimedOut={setIsTimedOut} size="icon-highlight" variant="highlighted" buttonChild={<Package className="size-5" />} />;
};
ProjectControllerButtons.RerunProject = function ProjectControllerRerunProject<TData extends StatusResponse>({
  mutationOptions
}: {
  mutationOptions: MutationOptions<TData, Error, void>;
}) {
  const {
    slug,
    project,
    isTimedOut,
    setIsTimedOut
  } = useProjectControllerContext();
  return <RerunProject mutationOptions={mutationOptions} slug={slug} project={project} isTimedOut={isTimedOut} setIsTimedOut={setIsTimedOut} buttonChild={<RotateCcw className="size-5" />} toolTipContent={`${m.project_ctrl_btns_rerun()} ${m.project_ctrl_btns_project()}`} />;
};